import { serializeJsonLd } from "@/lib/seo-organization";

type StructuredDataProps = {
  data: object | object[];
};

function JsonLdScript({ data }: { data: object }) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: serializeJsonLd(data) }}
    />
  );
}

/**
 * Renders Schema.org JSON-LD with XSS-safe serialisation.
 *
 * A list of schema nodes is emitted as one <script> **per node**, never as a
 * single script holding a top-level JSON array. Google accepts an array, but
 * plenty of other consumers assume the parsed value is an object and read
 * `data["@context"]` straight off it — on an array that is `undefined`, which
 * threw `TypeError: undefined is not an object (evaluating
 * 'r["@context"].toLowerCase')` in production Safari on every array-shaped
 * page (category, product, borough) while single-object pages stayed clean.
 * One node per script keeps both `@context` and `@type` at the top level.
 */
export function StructuredData({ data }: StructuredDataProps) {
  if (!Array.isArray(data)) {
    return <JsonLdScript data={data} />;
  }

  // Call sites build these arrays with spread conditionals, so tolerate holes.
  const nodes = data.filter(Boolean);

  if (nodes.length === 0) {
    return null;
  }

  return (
    <>
      {nodes.map((node, index) => (
        <JsonLdScript key={index} data={node} />
      ))}
    </>
  );
}
